import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import {
  PeriodontalExaminationChartFields,
  chartDefaultValues,
  PeriodontalExaminationChartFormDefaultValues,
} from 'constants/chart/periodontalExaminationChart';
import {
  TChartValues,
  TPeriodontalExaminationChartFormValues,
} from 'types/chart/periodontalExaminationChart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import { TEvaluationChartInfo } from 'types/apis/chart/studentEvaluationChart';
import { TTool } from 'types/apis/chart/studentPracticeChart';
import { TLine } from 'types/chart/pentool';
import useModal from 'hooks/useModal';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';
import Konva from 'konva';
import PeriodontalExaminationChart from '../PeriodontalExaminationChart';
import Pentool from 'components/pages/chart/Pentool';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isProfessor: boolean;
  isRegister: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  tool: TTool;
  setTool: (tool: TTool) => void;
  strokeActive: boolean;
  setStrokeActive: (active: boolean) => void;
  strokeWidth: number;
  setStrokeWidth: (stroke: number) => void;
  strokeColor: string;
  setStrokeColor: (color: string) => void;
  chartInfoResponse: TChartInfoResponse | null;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isEdit: boolean;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
  setStageImage: (ref: any) => void;
  setMandibularStageImage: (ref: any) => void;
};

const PeriodontalExaminationChartRegister = ({
  query,
  chartType,
  category,
  id,
  isProfessor,
  isRegister,
  setSubmitEndDate,
  tool,
  setTool,
  strokeActive,
  setStrokeActive,
  strokeWidth,
  setStrokeWidth,
  strokeColor,
  setStrokeColor,
  chartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isEdit,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
  setStageImage,
  setMandibularStageImage,
}: TProps) => {
  const { openModal } = useModal();

  const [drawingLines, setDrawingLines] = useState<TLine[]>([]);

  const [activeCell, setActiveCell] = useState({
    id: '',
    location: '',
    index: 0,
  });
  const [inputBoard, setInputBoard] = useState({
    active: false,
    type: '',
    top: 0,
    left: 0,
  });
  const [maxillaryValues, setMaxillaryValues] =
    useState<TChartValues>(chartDefaultValues);
  const [mandibularValues, setMandibularValues] =
    useState<TChartValues>(chartDefaultValues);
  const inputBoardRef = useRef<HTMLDivElement>(null);

  const { chartRegister } = useChartRegister();

  const methods = useForm<TPeriodontalExaminationChartFormValues>({
    defaultValues: PeriodontalExaminationChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;

  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const prevFormValuesRef =
    useRef<TPeriodontalExaminationChartFormValues | null>(null);

  const [maxLines, setMaxLines] = useState<TLine[]>([]);
  const [mandLines, setMandLines] = useState<TLine[]>([]);
  const stageRef = useRef<any>();
  const mandibularStageRef = useRef<any>();
  const penLayerRef = useRef<any>();

  useEffect(() => {
    setValue(
      PeriodontalExaminationChartFields.maxillaryValues,
      maxillaryValues
    );
  }, [maxillaryValues]);

  useEffect(() => {
    setValue(
      PeriodontalExaminationChartFields.mandibularValues,
      mandibularValues
    );
  }, [mandibularValues]);

  useEffect(() => {
    setValue(
      'maxillaryDrawing' as keyof TPeriodontalExaminationChartFormValues,
      stageRef.current?.toJSON() as TPeriodontalExaminationChartFormValues['maxillaryDrawing']
    );
    setStageImage(stageRef);
  }, [maxLines]);

  useEffect(() => {
    setValue(
      'mandibularDrawing' as keyof TPeriodontalExaminationChartFormValues,
      mandibularStageRef.current?.toJSON() as TPeriodontalExaminationChartFormValues['mandibularDrawing']
    );
    setMandibularStageImage(mandibularStageRef);
  }, [mandLines]);

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        if (
          (chartInfoResponse?.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ]?.chartData === null &&
          JSON.stringify(PeriodontalExaminationChartFormDefaultValues) ===
            JSON.stringify(formValuesRef.current)
        )
          return;
        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
          JSON.stringify(prevFormValuesRef.current)
        ) {
          onValid(true, formValuesRef.current);
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    const chartData: TPeriodontalExaminationChartFormValues | null =
      (chartInfo?.chartData as TPeriodontalExaminationChartFormValues | null) ||
      null;
    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);
    }
    if (!chartData) {
      reset();
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      setValue(
        key as keyof TPeriodontalExaminationChartFormValues,
        chartData[key as keyof TPeriodontalExaminationChartFormValues]
      );
    });

    setMaxillaryValues(
      'maxillaryValues' in chartInfo.chartData
        ? (chartInfo.chartData.maxillaryValues as TChartValues)
        : chartDefaultValues
    );

    setMandibularValues(
      'mandibularValues' in chartInfo.chartData
        ? (chartInfo.chartData.mandibularValues as TChartValues)
        : chartDefaultValues
    );

    if (!chartData.maxillaryDrawing || !chartData.mandibularDrawing) {
      setIsLoading(false);
      return;
    }

    const maxillaryDrawingData = JSON.parse(
      chartData.maxillaryDrawing as string
    );

    const mandibularDrawingData = JSON.parse(
      chartData.mandibularDrawing as string
    );

    // 데이터에서 라인을 하나라도 가지고 있는 레이어가 있다면 추출
    const maxIncludeLineLayers = maxillaryDrawingData.children.filter(
      (layer: any) => {
        return layer.children.some(
          (layerChild: any) => layerChild.className === 'Line'
        );
      }
    );

    const mandIncludeLineLayers = mandibularDrawingData.children.filter(
      (layer: any) => {
        return layer.children.some(
          (layerChild: any) => layerChild.className === 'Line'
        );
      }
    );

    const newMaxLines: TLine[] = [];

    maxIncludeLineLayers.forEach((lineLayer: any) => {
      lineLayer.children.forEach((lineLayerChild: any) => {
        if (lineLayerChild.className !== 'Line') {
          return;
        }
        // 각 라인으로 새로운 라인 만들기
        const newLine = new Konva.Line(lineLayerChild);

        // 라인의 attrs 에서 points 만 추출
        newMaxLines.push({
          points: newLine.attrs.points,
          strokeColor: newLine.attrs.stroke,
          strokeWidth: newLine.attrs.strokeWidth,
          tool: !newLine.attrs.globalCompositeOperation ? 'pen' : 'eraser',
        });
      });
    });
    // 라인의 attrs 에서 얻은 points 로 state 변경하기
    setMaxLines(newMaxLines);
    setDrawingLines([...drawingLines, ...newMaxLines]);

    const newMandLines: TLine[] = [];

    mandIncludeLineLayers.forEach((lineLayer: any) => {
      lineLayer.children.forEach((lineLayerChild: any) => {
        if (lineLayerChild.className !== 'Line') {
          return;
        }
        // 각 라인으로 새로운 라인 만들기
        const newLine = new Konva.Line(lineLayerChild);

        // 라인의 attrs 에서 points 만 추출
        newMandLines.push({
          points: newLine.attrs.points,
          strokeColor: newLine.attrs.stroke,
          strokeWidth: newLine.attrs.strokeWidth,
          tool: !newLine.attrs.globalCompositeOperation ? 'pen' : 'eraser',
        });
      });
    });
    // 라인의 attrs 에서 얻은 points 로 state 변경하기
    setMandLines(newMandLines);
    setDrawingLines([...drawingLines, ...newMandLines]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (activeCell.id === '') return;
      const position = getPosition(activeCell.id, activeCell.index);

      if (position) {
        const { top, left, height } = position;

        setInputBoard((prevState) => ({
          active: prevState.active,
          type: prevState.type,
          top: top + height,
          left: left,
        }));
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [activeCell.id]);

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (
        inputBoardRef.current &&
        !inputBoardRef.current.contains(e.target as Node)
      ) {
        closeInputBoard();
      }
    };
    window.addEventListener('mousedown', clickOutside);
    return () => window.removeEventListener('mousedown', clickOutside);
  }, [inputBoardRef]);

  const getPosition = (id: string, index: number) => {
    const el = document.getElementById(id)?.children[index + 1];
    if (!el) return;
    const { top, left, height } = el.getBoundingClientRect();

    return { top, left, height };
  };

  const closeInputBoard = () => {
    setInputBoard({ active: false, type: '', top: 0, left: 0 });
    setActiveCell({ id: '', location: '', index: 0 });
  };

  // 차트 조회
  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    setChartInfo(chartInfo);
  }, [activeTabIndex, chartInfoResponse]);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TPeriodontalExaminationChartFormValues
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          {!isProfessor && isRegister && (
            <Pentool
              greenPen={true}
              setTool={setTool}
              strokeActive={strokeActive}
              setStrokeActive={setStrokeActive}
              setStrokeWidth={setStrokeWidth}
              setStrokeColor={setStrokeColor}
              isRegister={
                (!isProfessor && isRegister && !isReadOnly) ||
                (isProfessor && !isRegister && isEdit)
              }
              feedbackLines={drawingLines}
              setFeedbackLines={setDrawingLines}
              maxLines={maxLines}
              setMaxLines={setMaxLines}
              mandLines={mandLines}
              setMandLines={setMandLines}
            />
          )}
          <PeriodontalExaminationChart
            tool={tool}
            strokeWidth={strokeWidth}
            strokeColor={strokeColor}
            strokeActive={strokeActive}
            maxLines={maxLines}
            setMaxLines={setMaxLines}
            mandLines={mandLines}
            setMandLines={setMandLines}
            drawingLines={drawingLines}
            setDrawingLines={setDrawingLines}
            stageRef={stageRef}
            mandibularStageRef={mandibularStageRef}
            penLayerRef={penLayerRef}
            maxillaryValues={maxillaryValues}
            setMaxillaryValues={setMaxillaryValues}
            mandibularValues={mandibularValues}
            setMandibularValues={setMandibularValues}
            isRegister={isRegister}
            isReadOnly={isReadOnly}
          />

          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default PeriodontalExaminationChartRegister;
